import React, { useState, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
  Box,
  Typography,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

export interface User {
  id: string;
  displayName: string;
  mail: string;
  jobTitle?: string;
  department?: string;
}

interface UserAutocompleteProps {
  value?: User[];
  onChange: (users: User[]) => void;
  label?: string;
  placeholder?: string;
}

const UserAutocomplete: React.FC<UserAutocompleteProps> = ({
  value = [],
  onChange,
  label = 'Search for people',
  placeholder = 'Type to search...',
}) => {
  const { getAccessToken } = useAuth();
  const [options, setOptions] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Search users via API
  const searchUsers = useCallback(
    async (query: string) => {
      // Special case: allow single asterisk to search for all users
      if (!query || (query.length < 2 && query !== '*')) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error('No authentication token available');
        }

        const params = new URLSearchParams({
          q: query,
          limit: '20',
          offset: '0',
        });

        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:9110'}/api/proposal/users/search?${params}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }

        const data = await response.json();
        const mappedUsers = (data.items || []).map((u: any) => ({
          id: u.user_id,
          displayName: u.display_name,
          mail: u.email,
          jobTitle: u.job_title,
          department: u.department,
        }));

        setOptions(mappedUsers);
      } catch (err) {
        console.error('Error fetching users:', err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [getAccessToken]
  );

  // Handle input change with debouncing
  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string) => {
      setInputValue(newInputValue);

      // Debounce search
      const timeoutId = setTimeout(() => {
        searchUsers(newInputValue);
      }, 300);

      return () => clearTimeout(timeoutId);
    },
    [searchUsers]
  );

  return (
    <Autocomplete
      multiple
      value={value}
      onChange={(_event, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      loading={loading}
      getOptionLabel={(option) => option.displayName}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x} // Disable built-in filtering (we filter server-side)
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <PersonIcon fontSize="small" color="action" />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {option.displayName}
              </Typography>
              {option.jobTitle && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {option.jobTitle}
                  {option.department && ` • ${option.department}`}
                </Typography>
              )}
            </Box>
          </Box>
        </li>
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.displayName}
            size="small"
          />
        ))
      }
      noOptionsText={
        inputValue.length < 2 && inputValue !== '*'
          ? 'Type at least 2 characters (or * for all)'
          : 'No users found'
      }
    />
  );
};

export default UserAutocomplete;
